import React, { lazy, Suspense, memo, useEffect, useRef, useState } from "react";

// Import critical above-the-fold components immediately
import HeroSection from "./HeroSection";
import AboutSection from "./AboutSection";
import CatalogsSection from "./CatalogsSection";

// Lazy load only the bottom sections that are truly below the fold
const ApplicationSolutionsSection = lazy(() =>
  import("./ApplicationSolutionsSection")
);
const UniqueValueSection = lazy(() => import("./UniqueValueSection"));
const ContactSection = lazy(() => import("./ContactSection"));

// Minimal loading component to reduce layout shift
const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "1rem",
      minHeight: "100px",
      opacity: 0.6,
    }}
  >
    <div
      style={{
        width: "24px",
        height: "24px",
        border: "2px solid #f3f3f3",
        borderTop: "2px solid #333",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }}
    />
  </div>
);

const HomePage = memo(() => {
  const [themeDark, setThemeDark] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const checkScroll = () => {
      requestAnimationFrame(() => {
        const viewMid = window.innerHeight * 0.45;

        const lightSections = container.querySelectorAll("[data-theme-light]");
        let inLight = false;
        lightSections.forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (rect.top < viewMid && rect.bottom > viewMid) inLight = true;
        });

        if (inLight) {
          setThemeDark(false);
          return;
        }

        const darkSections = container.querySelectorAll("[data-theme-dark]");
        let inDark = false;
        darkSections.forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (rect.top < viewMid && rect.bottom > viewMid) inDark = true;
        });
        setThemeDark(inDark);
      });
    };

    const onScroll = () => checkScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", checkScroll);
    checkScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  return (
    <>
      <div
        ref={scrollRef}
        className={`App landing-scroll${themeDark ? " theme-dark" : ""}`}
      >
        {/* Začetek: črna + bela pisava (Hero + O nas) */}
        <div data-theme-dark>
          <HeroSection />
        </div>
        <div data-theme-dark>
          <AboutSection />
        </div>

        {/* Izdelki: belo ozadje + črna pisava (edina svetla sekcija ob prehodu) */}
        <div data-theme-light>
          <Suspense fallback={<LoadingSpinner />}>
            <ApplicationSolutionsSection />
          </Suspense>
        </div>

        {/* Kaj nas ločuje: vedno belo ozadje + črna pisava */}
        <div data-theme-light>
          <Suspense fallback={<LoadingSpinner />}>
            <UniqueValueSection />
          </Suspense>
        </div>

        {/* Katalogi: belo ozadje */}
        <div data-theme-light>
          <CatalogsSection />
        </div>

        {/* Contact: vedno temna, brez scroll efekta */}
        <Suspense fallback={<LoadingSpinner />}>
          <ContactSection />
        </Suspense>
      </div>
    </>
  );
});

HomePage.displayName = "HomePage";

export default HomePage;

